import { forwardRef, useId, useRef } from 'react';
import { cn, focusRing } from '../utils/cn';
import { Icon, type IconName } from './Icon';

export interface TabItem {
  id: string;
  label: string;
  icon?: IconName;
  /** Rendered as a count chip, e.g. "Processes (12)" in the reference screens. */
  count?: number | null;
  disabled?: boolean;
}

export interface TabsProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  items: TabItem[];
  value: string;
  onChange: (id: string) => void;
  /** `page` renders the underlined product-360 style; `panel` renders a segmented control. */
  appearance?: 'page' | 'panel';
  ariaLabel: string;
}

export const Tabs = forwardRef<HTMLDivElement, TabsProps>(function Tabs(
  { items, value, onChange, appearance = 'page', ariaLabel, className, ...rest },
  ref,
) {
  const baseId = useId();
  const listRef = useRef<HTMLDivElement>(null);

  // Roving focus: arrow keys move between tabs, matching the ARIA tabs pattern.
  function onKeyDown(event: React.KeyboardEvent) {
    const enabled = items.filter((item) => !item.disabled);
    const currentIndex = enabled.findIndex((item) => item.id === value);
    if (currentIndex === -1) return;
    let nextIndex: number | null = null;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % enabled.length;
    else if (event.key === 'ArrowLeft')
      nextIndex = (currentIndex - 1 + enabled.length) % enabled.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = enabled.length - 1;
    if (nextIndex === null) return;
    event.preventDefault();
    const next = enabled[nextIndex];
    if (!next) return;
    onChange(next.id);
    listRef.current
      ?.querySelector<HTMLButtonElement>(`#${CSS.escape(`${baseId}-tab-${next.id}`)}`)
      ?.focus();
  }

  return (
    <div
      ref={ref}
      className={cn(appearance === 'page' && 'border-b border-line', className)}
      {...rest}
    >
      <div
        ref={listRef}
        role="tablist"
        aria-label={ariaLabel}
        onKeyDown={onKeyDown}
        className={cn(
          'flex items-center overflow-x-auto',
          appearance === 'page'
            ? 'gap-1'
            : 'gap-1 rounded-md border border-line bg-surface-sunken p-1',
        )}
      >
        {items.map((item) => {
          const selected = item.id === value;
          return (
            <button
              key={item.id}
              id={`${baseId}-tab-${item.id}`}
              role="tab"
              type="button"
              aria-selected={selected}
              aria-controls={`${baseId}-panel-${item.id}`}
              tabIndex={selected ? 0 : -1}
              disabled={item.disabled}
              onClick={() => onChange(item.id)}
              className={cn(
                'inline-flex items-center gap-1.5 whitespace-nowrap text-body font-medium transition-colors duration-fast ease-standard',
                'disabled:cursor-not-allowed disabled:text-content-disabled',
                focusRing,
                appearance === 'page'
                  ? cn(
                      'border-b-2 px-3 pb-2.5 pt-2',
                      selected
                        ? 'border-action text-action'
                        : 'border-transparent text-content-secondary hover:border-line-strong hover:text-content',
                    )
                  : cn(
                      'rounded-sm px-3 py-1.5 text-dense-lg',
                      selected
                        ? 'bg-surface text-content shadow-card'
                        : 'text-content-secondary hover:text-content',
                    ),
              )}
            >
              {item.icon ? <Icon name={item.icon} size={15} /> : null}
              {item.label}
              {item.count !== undefined && item.count !== null ? (
                <span
                  className={cn(
                    'rounded-sm px-1.5 text-micro font-semibold',
                    selected
                      ? 'bg-action-subtle text-action'
                      : 'bg-surface-sunken text-content-muted',
                  )}
                >
                  {item.count.toLocaleString('en-US')}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
});

export interface TabPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  tabsId: string;
  id: string;
  active: boolean;
}

export const TabPanel = forwardRef<HTMLDivElement, TabPanelProps>(function TabPanel(
  { tabsId, id, active, className, children, ...rest },
  ref,
) {
  if (!active) return null;
  return (
    <div
      ref={ref}
      role="tabpanel"
      id={`${tabsId}-panel-${id}`}
      aria-labelledby={`${tabsId}-tab-${id}`}
      tabIndex={0}
      className={cn(focusRing, className)}
      {...rest}
    >
      {children}
    </div>
  );
});
